<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-28
-->

<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from '#imports';
import type { Course } from '~/types/courses';

defineProps<{
  course: Course;
}>();

defineEmits<{
  (e: 'toggle-flip'): void;
}>();

const { t } = useI18n();

const imageLoadErrors = ref<Record<string, boolean>>({});

const secondsToTime = (s: number | null | undefined) => {
  if (s == null) return '';
  const h = Math.floor(s / 3600)
    .toString()
    .padStart(2, '0');
  const m = Math.floor((s % 3600) / 60)
    .toString()
    .padStart(2, '0');
  return `${h}:${m}`;
};

const formatDays = (days: string[] | null | undefined) => {
  if (!days || !days.length) return '';
  if (days.length === 7) {
    return t('courses.daily');
  }
  return days.map((d) => t(`courses.days.${d}`)).join(', ');
};
</script>

<template>
  <div
    class="card-back absolute inset-0 flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-md transition-shadow duration-300 group-hover:shadow-lg"
    @click="$emit('toggle-flip')"
  >
    <!-- Back Side Header -->
    <div class="mb-4 flex shrink-0 items-center justify-between border-b border-gray-50 pb-3">
      <div class="flex flex-col">
        <h4 class="text-sm font-black text-gray-950">
          {{ t('courses.instructorsAndSubjects') }}
        </h4>
        <span class="text-[11px] font-medium text-slate-500">
          {{ course.total_courses ?? course.courses_count ?? course.courses?.length ?? 0 }}
          {{ t('courses.coursesInPackage') }}
        </span>
      </div>
      <!-- Back Flip Trigger Button -->
      <button
        type="button"
        class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-slate-100 text-slate-600 transition duration-200 hover:bg-slate-200 hover:text-slate-900 focus:outline-none"
        @click.stop="$emit('toggle-flip')"
      >
        <Icon name="lucide:x" class="h-4 w-4" />
      </button>
    </div>

    <!-- Scrollable list of sub-courses -->
    <div class="flex-1 scrollbar-thin space-y-3.5 overflow-y-auto pr-1.5">
      <div
        v-for="sub in course.courses"
        :key="sub._id"
        class="flex flex-col gap-1.5 rounded-xl border border-slate-50 bg-slate-50/50 p-2.5"
      >
        <!-- Header Info -->
        <div class="flex items-center gap-2.5">
          <!-- Teacher Photo -->
          <div class="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-white bg-white shadow-xs">
            <AppImage
              v-if="sub.teacher?.photo && !imageLoadErrors[sub.teacher._id]"
              :src="sub.teacher.photo"
              :alt="sub.teacher.name"
              img-class="h-full w-full object-cover"
              loader-size="h-5 w-5"
              loader-overlay-class="bg-slate-50"
              loader-text=""
              @error="imageLoadErrors[sub.teacher._id] = true"
            />
            <div
              v-else
              class="flex h-full w-full items-center justify-center bg-brand-blue/5 font-sans text-xs font-bold text-brand-blue uppercase"
            >
              {{ sub.teacher?.name ? sub.teacher.name.substring(0, 2) : '👨‍🏫' }}
            </div>
          </div>

          <!-- Name & Subject -->
          <div class="min-w-0 flex-1">
            <div class="truncate text-xs font-black text-slate-900">
              {{ sub.teacher?.name || t('courses.na') }}
            </div>
            <div class="text-[10px] font-bold text-brand-blue uppercase">
              {{ sub.subject?.name || t('courses.na') }}
            </div>
          </div>
        </div>

        <!-- Sub-course Title -->
        <div class="line-clamp-1 text-[11px] font-medium text-slate-600">
          {{ sub.title }}
        </div>

        <!-- Sub-course Time details -->
        <div
          v-if="sub.time && (sub.time.days?.length || sub.time.start != null)"
          class="flex items-center gap-1.5 rounded-md bg-white/70 px-1.5 py-1 text-[10px] text-slate-500"
        >
          <Icon name="lucide:clock" class="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span class="truncate font-sans font-medium">
            {{ formatDays(sub.time.days) }} ({{ secondsToTime(sub.time.start) }} - {{ secondsToTime(sub.time.end) }})
          </span>
        </div>

        <!-- Features Icon Row for Sub-course -->
        <div class="mt-1 grid grid-cols-5 gap-1 rounded-lg border border-slate-100/50 bg-white p-1.5 shadow-xs">
          <!-- Videos -->
          <div class="flex min-w-0 flex-col items-center text-center">
            <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-blue/5 text-brand-blue">
              <Icon name="lucide:video" class="h-4 w-4" />
            </div>
            <span class="mt-1 w-full truncate text-[8px] leading-tight font-black text-slate-800">
              {{ sub.total_video || 0 }} {{ t('courses.vids') }}
            </span>
          </div>

          <!-- PDF Docs -->
          <div class="flex min-w-0 flex-col items-center text-center">
            <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-blue/5 text-brand-blue">
              <Icon name="lucide:file-text" class="h-4 w-4" />
            </div>
            <span class="mt-1 w-full truncate text-[8px] leading-tight font-black text-slate-800">
              {{ sub.total_pdf || 0 }} PDF
            </span>
          </div>

          <!-- Support -->
          <div class="flex min-w-0 flex-col items-center text-center">
            <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-blue/5 text-brand-blue">
              <Icon name="lucide:headphones" class="h-4 w-4" />
            </div>
            <span class="mt-1 w-full truncate text-[8px] leading-tight font-black text-slate-800"> E-Teach </span>
          </div>

          <!-- Quizzes -->
          <div class="flex min-w-0 flex-col items-center text-center">
            <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-blue/5 text-brand-blue">
              <Icon name="lucide:help-circle" class="h-4 w-4" />
            </div>
            <span class="mt-1 w-full truncate text-[8px] leading-tight font-black text-slate-800">
              {{ t('courses.quizzes') }}
            </span>
          </div>

          <!-- Exam -->
          <div class="flex min-w-0 flex-col items-center text-center">
            <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-blue/5 text-brand-blue">
              <Icon name="lucide:graduation-cap" class="h-4 w-4" />
            </div>
            <span class="mt-1 w-full truncate text-[8px] leading-tight font-black text-slate-800">
              {{ t('courses.exams') }}
            </span>
          </div>
        </div>
      </div>
      <!-- Show remaining courses count if there are more than 10 -->
      <div
        v-if="(course.total_courses ?? course.courses_count ?? 0) > 10"
        class="flex items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/30 p-3 font-sans text-xs font-bold text-slate-500"
      >
        +{{ (course.total_courses ?? course.courses_count ?? 0) - 10 }} {{ t('courses.more') }}
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Hide scrollbar for sub-courses list */
.scrollbar-thin::-webkit-scrollbar {
  display: none;
}

.scrollbar-thin {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
